import { FileText, Package } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "./AppShell";
import { Button, cx } from "./ui";
import { api, isDemo, type B2bLoadCarrier, type B2bOrderInfo, type B2bShipping } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
export const SHIPPING_LABEL: Record<B2bShipping, string> = { freight: "Spedition", parcel: "Paket" };
export const LOAD_CARRIER_LABEL: Record<B2bLoadCarrier, string> = { pallet: "Palette", carton: "Karton" };
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Liefertermin kurz, z. B. „Mi 14.10., 08:00–12:00“ */
export function deliveryShort(b: Pick<B2bOrderInfo, "deliveryDate" | "windowFrom" | "windowTo">) {
  if (!b.deliveryDate) return null;
  const d = new Date(`${b.deliveryDate}T12:00:00Z`);
  const win = b.windowFrom && b.windowTo ? `, ${b.windowFrom}–${b.windowTo}` : b.windowFrom ? `, ab ${b.windowFrom}` : b.windowTo ? `, bis ${b.windowTo}` : "";
  return `${WD[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${win}`;
}
export const requirementTexts = (r: B2bOrderInfo["requirements"]) => [
  ...(r.minShelfLifeDays ? [`MHD bei Ankunft mind. ${r.minShelfLifeDays} Tage`] : []),
  ...(r.fullCartonsOnly ? ["nur ganze Kartons"] : []),
  ...(r.singleBatch ? ["je Artikel nur eine Charge"] : []),
];

/** Am Auftrag: B2B-Angaben und Lieferschein */
export function OrderB2bSection({ orderId, b2b }: { orderId: string; b2b: B2bOrderInfo }) {
  const { tenantId } = useTenant();
  const req = requirementTexts(b2b.requirements);
  const rows: [string, string | null][] = [
    [msg("Abnehmer"), `${b2b.customerName}${b2b.customerNumber ? ` (Kd-Nr. ${b2b.customerNumber})` : ""}`],
    [msg("Bestell-Nr."), b2b.purchaseOrderNumber],
    [msg("Liefertermin"), deliveryShort(b2b)],
    [msg("Versand"), `${SHIPPING_LABEL[b2b.shipping]} · ${LOAD_CARRIER_LABEL[b2b.loadCarrier]}`],
    [msg("Warenannahme"), b2b.receivingHours],
    [msg("Voranmeldung"), b2b.avisRequired ? b2b.avisContact ?? "erforderlich" : null],
    [msg("GLN"), b2b.gln],
    [msg("Vorgaben"), req.length ? req.join(" · ") : null],
  ];
  return (
    <section className="rounded-xl border border-border p-4">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-[13.5px] flex items-center gap-2 flex-1"><Package className="size-4 text-fg-muted" /> {t("B2B-Lieferung")}</p>
        <a href={isDemo ? undefined : api.b2b.deliveryNoteUrl(tenantId, orderId)} target="_blank" rel="noreferrer" onClick={isDemo ? () => toast.info("Im Demo-Modus nicht verfügbar") : undefined}>
          <Button size="sm" icon={<FileText className="size-3.5" />}>{t("Lieferschein")}</Button>
        </a>
      </div>
      <dl className="mt-2.5 grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-[13px]">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-fg-muted">{k}</dt>
            <dd className={cx(k === "Bestell-Nr." && "font-mono", k === "Vorgaben" && "text-warning")}>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
